import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import {
    Button,
    Callout,
    EmptyState,
    Input,
    Listbox,
    type ListboxItem,
    Modal,
    Skeleton,
} from '@/components/ui';
import { useAuth } from '@/auth/useAuth';
import { useRuntimeConfig } from '@/config/useRuntimeConfig';
import { fetchOrganizations, fetchProvisioningStatus } from '@/api/orgAdmin';
import { classifyMoveCandidate, MOVE_BLOCK_LABELS } from './dragRules';
import type { HierarchyPageVM } from './useHierarchyPage';
import type { MoveCandidate } from './types';
import hz from './Hierarchy.module.css';
import styles from './MoveOrgDialog.module.css';

export interface MoveOrgDialogProps {
    vm: HierarchyPageVM;
}

/**
 * EAA-1818: frame 31's "Chuyển đến…" — a searchable parent picker, reachable from
 * the row context menu and from ⌘M/Ctrl+M on a focused row.
 *
 * WHY A MODAL LIST RATHER THAN THE TREE'S KEYBOARD DRAG. The tree's Space/Arrow/
 * Enter state machine navigates DROP SLOTS in a rendered, lazily loaded tree; a
 * picker navigates a flat, server-searched candidate list where "before/after"
 * has no meaning. Reusing that machine here would mean teaching it a second
 * coordinate space for no user-visible gain, so the dialog is plain list
 * semantics — arrow/tab through rows, Enter on "Chuyển" — while the write itself
 * goes through the SAME `useOrgMove` mutation the drag path uses, so there is
 * exactly one place a move is performed.
 *
 * CANDIDATES come from `GET /organizations?search=` (server-side, so the picker
 * reaches orgs the tree has not expanded), graded by `classifyMoveCandidate` —
 * the same rules the drag path applies. `cross-root` grading is best-effort by
 * nature (the ancestor chain is only walkable where loaded, see `dragRules.ts`),
 * which is precisely why the amber panel below tells the user the destination's
 * app scope cannot be previewed, and why the server remains the final authority.
 */
export function MoveOrgDialog({ vm }: MoveOrgDialogProps) {
    const { t } = useTranslation();
    const config = useRuntimeConfig();
    const { accessToken } = useAuth();
    const { moveDialogNode, closeMoveDialog, tree, knownOrgs, unitTypesById, requestMove } = vm;
    const [query, setQuery] = useState('');
    const [chosenId, setChosenId] = useState<string | null>(null);

    const open = Boolean(moveDialogNode);
    const trimmed = query.trim();

    const candidatesQuery = useQuery({
        queryKey: ['org-move-candidates', accessToken, trimmed],
        queryFn: () =>
            fetchOrganizations(config.apiBase, accessToken as string, {
                ...(trimmed ? { search: trimmed } : {}),
                pageSize: 50,
                ordering: 'name',
            }),
        enabled: open && Boolean(accessToken),
        retry: false,
    });

    const candidates = useMemo<MoveCandidate[]>(() => {
        const rows = candidatesQuery.data?.results ?? [];
        if (!moveDialogNode) return [];
        return rows.map((organization) => ({
            organization,
            // EAA-1838: `unitTypesById` is passed so a candidate whose type cannot
            // hold this one greys out here exactly as the drag path refuses it —
            // the picker and the tree must never disagree about a legal target.
            blockedBy: classifyMoveCandidate(
                moveDialogNode,
                organization,
                tree,
                knownOrgs,
                unitTypesById
            ),
        }));
    }, [candidatesQuery.data, moveDialogNode, tree, knownOrgs, unitTypesById]);

    /**
     * EAA-1839: candidate rows are the kit's `Listbox` now, so each candidate is
     * mapped onto its item shape. `rightLabel` (frame 31's "Sở · cấp cha hiện
     * tại") is resolved here rather than in the row renderer because it needs
     * `unitTypesById`, and computing it once per candidate per data change beats
     * once per candidate per render.
     */
    const candidateItems = useMemo<ListboxItem<MoveCandidate & { rightLabel: string }>[]>(
        () =>
            candidates.map((candidate) => {
                const { organization, blockedBy } = candidate;
                const unitTypeName = organization.unit_type
                    ? (unitTypesById.get(organization.unit_type)?.name ?? null)
                    : null;
                const reason = blockedBy ? MOVE_BLOCK_LABELS[blockedBy] : null;
                return {
                    id: organization.id,
                    data: {
                        ...candidate,
                        rightLabel: [unitTypeName, reason].filter(Boolean).join(' · '),
                    },
                    disabled: blockedBy !== null,
                    disabledReason: reason,
                };
            }),
        [candidates, unitTypesById]
    );

    const chosen =
        candidates.find((c) => c.organization.id === chosenId && c.blockedBy === null) ?? null;

    /**
     * The real "N tài khoản" figure frame 31 shows. `provisioning-status` is
     * per-organization, so this is the moved đơn vị's own active-assignment
     * count — the mock's static 124 replaced with the actual number. Shares the
     * `['org-provisioning', token, id]` key with the detail panel, so selecting
     * a row and then moving it costs one request, not two.
     */
    const accountsQuery = useQuery({
        queryKey: ['org-provisioning', accessToken, moveDialogNode?.id ?? null],
        queryFn: () =>
            fetchProvisioningStatus(
                config.apiBase,
                accessToken as string,
                moveDialogNode?.id as string
            ),
        enabled: open && Boolean(accessToken) && Boolean(moveDialogNode),
        retry: false,
    });
    const pendingAccounts = accountsQuery.data?.assignments.active ?? null;

    function handleClose() {
        setQuery('');
        setChosenId(null);
        closeMoveDialog();
    }

    function handleConfirm() {
        if (!moveDialogNode || !chosen) return;
        requestMove(moveDialogNode, chosen.organization.id, chosen.organization.name, 'dialog');
        handleClose();
    }

    const accountsLine =
        pendingAccounts === null
            ? t('console.hierarchy.moveDialog.accountsLineUnknown')
            : t('console.hierarchy.moveDialog.accountsLineKnown', {
                  value: pendingAccounts.toLocaleString('vi-VN'),
              });

    return (
        <Modal
            open={open}
            onCancel={handleClose}
            title={
                moveDialogNode
                    ? t('console.hierarchy.moveDialog.title', {
                          name: moveDialogNode.organization.name,
                      })
                    : ''
            }
            footer={
                <div className="hz-move-footer">
                    {/* EAA-1838: the note no longer promises that a missing cấp bậc
                        lets any move through — every đơn vị now has a type, so rule 4
                        actually applies. Saying otherwise would describe the old
                        model. */}
                    <span className="hz-move-footer-note">
                        {t('console.hierarchy.moveDialog.footerNote')}
                    </span>
                    {/* EAA-1804: kit `Button` instead of hand-written `<button className="portal-btn">`
                        — see AccountDetailModal.tsx for the mapping rationale. The row buttons
                        below (`MoveOrgDialog.module.css`'s `.row`) are a different,
                        non-portal-btn component and are left alone. */}
                    <span className="hz-move-footer-actions">
                        <Button onClick={handleClose}>{t('common.cancel')}</Button>
                        <Button type="primary" disabled={!chosen} onClick={handleConfirm}>
                            {t('console.hierarchy.moveDialog.confirmButton')}
                        </Button>
                    </span>
                </div>
            }
        >
            <p className={hz.cardNote}>{t('console.hierarchy.moveDialog.description')}</p>

            {/* EAA-1804: raw <input> with a hand-drawn magnifier → kit Input, icon
                moved into its `prefix` slot — same shape as `HierarchyPage`'s tree
                search box (`hz-search`), which this one also styles off. */}
            <Input
                className={`hz-search ${styles.search}`}
                type="text"
                placeholder={t('console.hierarchy.moveDialog.searchPlaceholder')}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                aria-label={t('console.hierarchy.moveDialog.searchAriaLabel')}
                prefix={
                    <svg
                        viewBox="0 0 24 24"
                        width="15"
                        height="15"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={1.8}
                        aria-hidden="true"
                    >
                        <circle cx="11" cy="11" r="7" />
                        <path d="m20 20-3.5-3.5" strokeLinecap="round" />
                    </svg>
                }
            />

            {/* EAA-1839: the hand-rolled `role="listbox"` / `role="option"` pair is
                now the kit's `Listbox`. The file docstring above promises "plain
                list semantics — arrow/tab through rows"; the arrow half was never
                actually implemented, and the kit supplies it. `optionsUnstyled`
                keeps the row chrome in `MoveOrgDialog.module.css`'s `.row` (the
                --hz-row-h height, the `--tint-item-selected` chosen fill with its
                inset ring, the muted blocked state), which the kit's inline option
                palette would otherwise override.

                EAA-1838: frame 31 puts the candidate's TIER on the right of each
                row — "Sở · cấp cha hiện tại" for the current parent, "Trường ✓" for
                the chosen one. So the type name is the primary label there and the
                block reason is appended to it, rather than the two competing for the
                slot. A candidate whose type has not loaded keeps the pre-existing
                behaviour (its mã, or the bare reason) rather than a placeholder —
                see `candidateItems` above, where `rightLabel` is built. */}
            <Listbox<MoveCandidate & { rightLabel: string }>
                className={styles.list}
                aria-label={t('console.hierarchy.moveDialog.listAriaLabel')}
                items={candidateItems}
                selectedId={chosenId}
                onSelect={(item) => setChosenId(item.id)}
                optionsUnstyled
                optionClassName={(_item, state) =>
                    [
                        styles.row,
                        state.disabled ? styles.rowBlocked : '',
                        state.selected && !state.disabled ? styles.rowChosen : '',
                    ]
                        .filter(Boolean)
                        .join(' ')
                }
                emptyState={
                    candidatesQuery.isLoading ? (
                        [0, 1, 2].map((i) => (
                            <span key={i} className={hz.skeletonRow}>
                                <Skeleton height="24px" width="24px" />
                                <Skeleton height="0.75rem" width={`${50 - i * 8}%`} />
                            </span>
                        ))
                    ) : (
                        <EmptyState title={t('console.hierarchy.moveDialog.noCandidatesTitle')} />
                    )
                }
                renderItem={(item, state) => (
                    <>
                        <span className={hz.avatar} data-muted={state.disabled || undefined}>
                            {item.data.organization.code.slice(0, 2).toUpperCase()}
                        </span>
                        <span className={hz.name}>{item.data.organization.name}</span>
                        {item.data.rightLabel ? (
                            <span className={styles.reason}>{item.data.rightLabel}</span>
                        ) : (
                            <code className={hz.code}>{item.data.organization.code}</code>
                        )}
                        {state.selected && !state.disabled && (
                            <svg
                                viewBox="0 0 24 24"
                                width="16"
                                height="16"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth={2.4}
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                aria-hidden="true"
                                className={styles.check}
                            >
                                <path d="m5 13 4 4L19 7" />
                            </svg>
                        )}
                    </>
                )}
            />

            {/* EAA-1818: fixed design copy, and it describes a real gap rather than
                a placeholder. portal-api has no move-preview endpoint (confirmed
                out of scope in EAA-1836), and app scope is per-organization and
                not inherited, so there is genuinely nothing to precompute. */}
            <Callout
                tone="warning"
                label={t('console.hierarchy.moveDialog.calloutLabel')}
                longLabel
            >
                {t('console.hierarchy.moveDialog.calloutBody')} {accountsLine}
            </Callout>
        </Modal>
    );
}

export default MoveOrgDialog;
